import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import usePagedRegister from "../usePagedRegister";
import "./PolicyMaintenanceWorkspace.css";

const rows = (value) =>
  Array.isArray(value) ? value : value?.results || [];

function PolicyMaintenanceWorkspace() {
  const [selected, setSelected] = useState(null);
  const [context, setContext] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [removeBox, setRemoveBox] = useState(false);
  const [removeForm, setRemoveForm] = useState({
    member: "",
    reason: "",
    effective_date: new Date().toISOString().slice(0, 10),
    confirmed: false,
  });
  const [replacementBox, setReplacementBox] = useState(false);
  const [replacementForm, setReplacementForm] = useState({
    member: "",
    reason: "",
    effective_date: new Date().toISOString().slice(0, 10),
    confirmed: false,
  });
  const [claimBox, setClaimBox] = useState(false);
  const [claimForm, setClaimForm] = useState({
    member_ids: [],
    incident_date: new Date().toISOString().slice(0, 10),
    payment_method: "",
    remarks: "",
    burial_order_number: "",
    death_register_number: "",
    burial_place: "",
    burial_service_by: "",
    burial_service_date: "",
    doctor_used: "",
    removal_time: "",
    notes: "",
    confirmed: false,
  });
  const [amendmentBox, setAmendmentBox] = useState(false);
  const [amendmentDirection, setAmendmentDirection] = useState("");
  const [amendmentQuote, setAmendmentQuote] = useState(null);
  const [amendmentForm, setAmendmentForm] = useState({
    plan: "",
    effective_date: new Date().toISOString().slice(0, 10),
    reason: "",
    confirmed: false,
  });
  const [documentsBox, setDocumentsBox] = useState(false);
  const [documentEligibility, setDocumentEligibility] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    document_type: "OTHER",
    title: "",
    file: null,
  });
  const [rejoinBox, setRejoinBox] = useState(false);
  const [rejoinPreview, setRejoinPreview] = useState(null);
  const [rejoinForm, setRejoinForm] = useState({
    effective_date: new Date().toISOString().slice(0, 10),
    reason: "",
    confirmed: false,
  });

  const {
    items: policies,
    reload: reloadPolicies,
    onScroll: policyScroll,
  } = usePagedRegister("policies/", {
    search,
    pageSize: 50,
  });

  const loadPolicies = async () => {
    const data = await reloadPolicies({ append: false });
    const requested = new URLSearchParams(
      window.location.search
    ).get("policy");
    const first =
      data.find((item) => String(item.id) === requested) ||
      data[0];
    if (first) await open(first.id);
  };

  const open = async (id) => {
    const response = await axiosInstance.get(`policy-maintenance/${id}/`);
    setSelected(response.data.policy);
    setContext(response.data);
  };

  useEffect(() => {
    loadPolicies().catch(() =>
      toast.error("Could not load Policy Maintenance.")
    );
  }, []);

  const filtered = policies;

  const fixedTermCashPlan = Boolean(
    selected &&
      (
        /CASH/.test(String(selected.policy_type || "").toUpperCase()) ||
        /PRE[- ]?CASH/.test(String(selected.plan_name || selected.plan?.name || "").toUpperCase())
      )
  );

  const maturityEligibility =
    documentEligibility?.MATURITY_CERTIFICATE || null;

  const maturityAvailable = Boolean(
    maturityEligibility?.eligible && selected?.status === "MATURED"
  );

  const maturityReason = maturityAvailable
    ? "Ready to generate the official maturity certificate."
    : (
        maturityEligibility?.reasons?.[0] ||
        (selected?.status !== "MATURED"
          ? `Available once this fixed-term Pre-Cash policy reaches MATURED status. Current status: ${selected?.status || "UNKNOWN"}.`
          : "Maturity certificate is not currently available.")
      );

  const evaluate = async () => {
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `policy-maintenance/${selected.id}/evaluate-status/`,
        {}
      );
      setSelected(response.data.policy);
      setContext((old) => ({
        ...old,
        policy: response.data.policy,
        status_assessment: response.data.assessment,
      }));
      toast.success(
        response.data.changed
          ? "Policy status updated by the status engine."
          : "Status checked. No change was required."
      );
      await loadPolicies();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Status evaluation failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const openRejoin = async () => {
    if (!["INACTIVE", "LAPSED", "SUSPENDED"].includes(selected?.status)) {
      toast.error("Only an inactive, lapsed or suspended policy can be rejoined.");
      return;
    }
    setBusy(true);
    try {
      const response = await axiosInstance.get(
        `policies/${selected.id}/rejoin/preview/`
      );
      setRejoinPreview(response.data);
      setRejoinForm({
        effective_date: new Date().toISOString().slice(0, 10),
        reason: "",
        confirmed: false,
      });
      setRejoinBox(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Could not prepare the rejoin preview."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitRejoin = async (event) => {
    event.preventDefault();
    if (!rejoinForm.confirmed) {
      toast.error("Confirm the rejoin and credit allocation preview.");
      return;
    }
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `policies/${selected.id}/rejoin/`,
        rejoinForm
      );
      const credit = response.data.credit_application || {};
      toast.success(
        `${response.data.detail} Credit applied: ${Number(
          credit.applied || 0
        ).toFixed(2)}.`
      );
      setRejoinBox(false);
      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Policy rejoin failed."
      );
    } finally {
      setBusy(false);
    }
  };


  const applyAvailableCredit = async () => {
    if (!selected?.id) return;
    setBusy(true);
    try {
      const response = await axiosInstance.get(
        `policies/${selected.id}/credit/preview/`
      );
      const preview = response.data?.credit_application || {};
      const applicable = Number(preview.applied || 0);

      if (applicable <= 0) {
        const untraceable = Number(preview.untraceable_credit || 0);
        toast.info(
          untraceable > 0
            ? `No traceable credit can be applied. Protected untraceable credit: ${untraceable.toFixed(2)}.`
            : "There is no receipt-backed credit available to apply to outstanding bills."
        );
        return;
      }

      const message = [
        `Apply available credit for ${selected.code}?`,
        "",
        `Current balance: ${Number(preview.starting_balance || 0).toFixed(2)}`,
        `Policy credit: ${Number(preview.starting_credit || 0).toFixed(2)}`,
        `Receipt-backed credit: ${Number(preview.traceable_credit || 0).toFixed(2)}`,
        `Credit to apply: ${applicable.toFixed(2)}`,
        `Balance after: ${Number(preview.remaining_balance || 0).toFixed(2)}`,
        `Credit remaining: ${Number(preview.remaining_credit || 0).toFixed(2)}`,
        `Protected untraceable credit: ${Number(preview.untraceable_credit || 0).toFixed(2)}`,
        "",
        "Only real unallocated receipt money will be moved onto the oldest outstanding bills. No receipt is deleted and protected credit is not wiped.",
      ].join("\n");

      if (!window.confirm(message)) return;

      const reason = window.prompt(
        "Authorised reason for applying available credit:",
        "APPLY RECEIPT-BACKED POLICY CREDIT TO OUTSTANDING BILLS"
      );
      if (!reason?.trim()) return;

      const applyResponse = await axiosInstance.post(
        `policies/${selected.id}/credit/apply/`,
        {
          confirmed: true,
          reason: reason.trim().toUpperCase(),
        }
      );

      const result = applyResponse.data?.result || {};
      toast.success(
        `${applyResponse.data?.detail || "Credit applied."} Applied: ${Number(
          result.applied || 0
        ).toFixed(2)}. Balance remaining: ${Number(
          result.remaining_balance || 0
        ).toFixed(2)}.`
      );

      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not apply available policy credit."
      );
    } finally {
      setBusy(false);
    }
  };

  const reverseLatestCreditApplication = async () => {
    if (!selected?.id) return;
    setBusy(true);
    try {
      const response = await axiosInstance.get(
        `policies/${selected.id}/credit/reversal/preview/`
      );
      const preview = response.data?.credit_reversal || {};

      if (!preview.available) {
        toast.info(
          preview.reason ||
            "There is no provenance-tracked credit application available for reversal."
        );
        return;
      }

      if (!preview.safe_to_reverse) {
        const blockers = (preview.blockers || []).join("\n• ");
        window.alert(
          `Credit reversal is blocked for ${selected.code}.\n\n• ${
            blockers || "The current ledger no longer matches the original application."
          }`
        );
        return;
      }

      const message = [
        `Reverse / cancel latest credit application for ${selected.code}?`,
        "",
        `Credit originally applied: ${Number(preview.applied || 0).toFixed(2)}`,
        `Original reason: ${preview.original_reason || "—"}`,
        `Bills affected: ${preview.changed_bills || 0}`,
        `Receipts affected: ${preview.changed_receipts || 0}`,
        `Allocations affected: ${preview.changed_allocations || 0}`,
        `Commission entries affected: ${preview.changed_commissions || 0}`,
        "",
        "The server restores the exact pre-application ledger only if nothing has changed since that application.",
        "EcoSure-linked financial changes are never reversed automatically.",
      ].join("\n");

      if (!window.confirm(message)) return;

      const reason = window.prompt(
        "Authorised reason for reversing / cancelling this credit application:",
        "CREDIT APPLICATION ENTERED IN ERROR"
      );
      if (!reason?.trim() || reason.trim().length < 5) return;

      const applyResponse = await axiosInstance.post(
        `policies/${selected.id}/credit/reversal/apply/`,
        {
          confirmed: true,
          reason: reason.trim().toUpperCase(),
        }
      );

      const result = applyResponse.data?.result || {};
      toast.success(
        `${applyResponse.data?.detail || "Credit application reversed."} Reversed: ${Number(
          result.reversed_amount || 0
        ).toFixed(2)}.`
      );

      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not reverse the policy credit application."
      );
    } finally {
      setBusy(false);
    }
  };

  const reconcileRejoinBalance = async () => {
    if (!selected?.id) return;
    setBusy(true);
    try {
      const previewResponse = await axiosInstance.get(
        `policies/${selected.id}/rejoin-balance/preview/`
      );
      const preview = previewResponse.data || {};

      if (preview.safe_to_apply === false) {
        toast.error(
          "This balance cannot be auto-cleared because affected bills are linked to EcoSure funding."
        );
        return;
      }

      const message = [
        `Auto-clear rejoin balance for ${selected.code}?`,
        "",
        `Recorded rejoin date: ${preview.rejoin_date}`,
        `Current balance: ${Number(preview.current_balance || 0).toFixed(2)}`,
        `Pre-rejoin arrears to clear: ${Number(preview.pre_rejoin_arrears_to_clear || 0).toFixed(2)}`,
        `Post-rejoin receipts: ${Number(preview.rejoin_receipt_total || 0).toFixed(2)}`,
        `Expected balance: ${Number(preview.expected_balance || 0).toFixed(2)}`,
        `Expected credit: ${Number(preview.expected_credit || 0).toFixed(2)}`,
        "",
        "Historical bills and receipts remain auditable. Only the rejoin-period ledger allocation will be corrected.",
      ].join("\\n");

      if (!window.confirm(message)) return;

      const reason = window.prompt(
        "Authorised reason for clearing the pre-rejoin balance:",
        "PRE-REJOIN ARREARS MUST NOT CARRY INTO THE REJOINED POLICY"
      );
      if (!reason?.trim()) return;

      const applyResponse = await axiosInstance.post(
        `policies/${selected.id}/rejoin-balance/apply/`,
        { confirmed: true, reason: reason.trim().toUpperCase() }
      );

      const result = applyResponse.data?.result || {};
      toast.success(
        `${applyResponse.data?.detail || "Rejoin balance corrected."} New balance: ${Number(result.expected_balance || 0).toFixed(2)}.`
      );
      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not auto-clear the rejoin balance."
      );
    } finally {
      setBusy(false);
    }
  };


  const openMemberRemoval = () => {
    const removable = context?.removable_members || [];
    if (!removable.length) {
      toast.info(
        "This policy has no active spouse, child or dependant eligible for removal."
      );
      return;
    }
    setRemoveForm({
      member: String(removable[0].id),
      reason: "",
      effective_date: new Date().toISOString().slice(0, 10),
      confirmed: false,
    });
    setRemoveBox(true);
  };

  const removeMember = async (event) => {
    event.preventDefault();
    if (!removeForm.confirmed) {
      toast.error("Confirm that you understand the removal impact.");
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post(
        `policy-maintenance/${selected.id}/members/${removeForm.member}/remove/`,
        {
          reason: removeForm.reason,
          effective_date: removeForm.effective_date,
        }
      );
      toast.success("Member removed. Future premium billing was adjusted.");
      setRemoveBox(false);
      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Member removal failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const selectedRemovalMember = (context?.removable_members || []).find(
    (member) => String(member.id) === String(removeForm.member)
  );


  const openPolicyholderReplacement = () => {
    if (!context?.replacement_allowed) {
      toast.error(
        context?.replacement_block_reason ||
          "Policyholder replacement is not allowed for this policy."
      );
      return;
    }
    const members = context?.replacement_members || [];
    if (!members.length) {
      toast.error(
        "There is no active spouse, child or dependant eligible for replacement."
      );
      return;
    }
    setReplacementForm({
      member: String(members[0].id),
      reason: "",
      effective_date: new Date().toISOString().slice(0, 10),
      confirmed: false,
    });
    setReplacementBox(true);
  };

  const replacePolicyholder = async (event) => {
    event.preventDefault();
    if (!replacementForm.confirmed) {
      toast.error("Confirm the policyholder replacement.");
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post(
        `policy-maintenance/${selected.id}/replace-policyholder/`,
        replacementForm
      );
      toast.success(
        "Policyholder replaced and the policyholder login was reassigned."
      );
      setReplacementBox(false);
      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Policyholder replacement failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const selectedReplacementMember = (
    context?.replacement_members || []
  ).find(
    (member) =>
      String(member.id) === String(replacementForm.member)
  );


  const openEditSelected = () => {
    window.location.assign(`/policies?policy=${selected.id}&edit=1`);
  };

  const openClaim = () => {
    const eligibility = context?.claim_eligibility;
    if (!eligibility?.policy_eligible) {
      toast.error(
        (eligibility?.policy_reasons || ["Claim is not allowed."]).join(" ")
      );
      return;
    }
    const eligibleMembers = (eligibility?.members || []).filter(
      (member) => member.eligible
    );
    if (!eligibleMembers.length) {
      toast.error("No covered member is currently eligible for a claim.");
      return;
    }
    setClaimForm({
      member_ids: [eligibleMembers[0].id],
      incident_date: new Date().toISOString().slice(0, 10),
      payment_method: "",
      remarks: "",
      burial_order_number: "",
      death_register_number: "",
      burial_place: "",
      burial_service_by: "",
      burial_service_date: "",
      doctor_used: "",
      removal_time: "",
      notes: "",
      confirmed: false,
    });
    setClaimBox(true);
  };

  const submitClaim = async (event) => {
    event.preventDefault();
    if (!claimForm.confirmed) {
      toast.error("Confirm the deceased-member and claim information.");
      return;
    }
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `policies/${selected.id}/create-claims/`,
        claimForm
      );
      const codes = (response.data || []).map((item) => item.code).join(", ");
      toast.success(`Claim created successfully: ${codes}`);
      setClaimBox(false);
      await open(selected.id);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Claim registration failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const claimMembers = (context?.claim_eligibility?.members || []).filter(
    (member) => member.eligible
  );


  const openAmendment = (direction) => {
    const plans = context?.available_plans || [];
    if (!plans.length) {
      toast.error("There are no other active plans available.");
      return;
    }
    setAmendmentDirection(direction);
    setAmendmentQuote(null);
    setAmendmentForm({
      plan: String(plans[0].id),
      effective_date: new Date().toISOString().slice(0, 10),
      reason: "",
      confirmed: false,
    });
    setAmendmentBox(true);
  };

  const quoteAmendment = async () => {
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `policy-maintenance/${selected.id}/plan-amendment/quote/`,
        {
          plan: amendmentForm.plan,
          effective_date: amendmentForm.effective_date,
        }
      );
      setAmendmentQuote(response.data);
      if (response.data.direction !== amendmentDirection) {
        toast.error(
          `This plan is a ${response.data.direction.toLowerCase()}, not a ${amendmentDirection.toLowerCase()}.`
        );
      }
    } catch (error) {
      setAmendmentQuote(null);
      toast.error(
        error?.response?.data?.detail ||
          "Could not calculate the policy amendment."
      );
    } finally {
      setBusy(false);
    }
  };

  const applyAmendment = async (event) => {
    event.preventDefault();
    if (!amendmentQuote) {
      toast.error("Calculate and review the amendment first.");
      return;
    }
    if (amendmentQuote.direction !== amendmentDirection) {
      toast.error(
        `The selected plan is not a valid ${amendmentDirection.toLowerCase()}.`
      );
      return;
    }
    if (!amendmentForm.confirmed) {
      toast.error("Confirm the policy amendment.");
      return;
    }

    setBusy(true);
    try {
      await axiosInstance.post(
        `policy-maintenance/${selected.id}/plan-amendment/apply/`,
        {
          ...amendmentForm,
          direction: amendmentDirection,
        }
      );
      toast.success(
        `Policy ${amendmentDirection.toLowerCase()} completed successfully.`
      );
      setAmendmentBox(false);
      await open(selected.id);
      await reloadPolicies({ append: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Policy amendment failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const openDocuments = async () => {
    setDocumentsBox(true);
    setDocumentEligibility(null);

    if (!selected?.id) return;

    try {
      const response = await axiosInstance.get(
        `policy-maintenance/${selected.id}/documents/eligibility/`
      );
      setDocumentEligibility(response.data || {});
    } catch (error) {
      setDocumentEligibility({});
      toast.error(
        error?.response?.data?.detail ||
          "Could not load document eligibility."
      );
    }
  };

  const openGeneratedDocument = async (type, language = "ENGLISH") => {
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `policy-maintenance/${selected.id}/documents/generated/${type}/`,
        { language },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      await open(selected.id, "documents");
    } catch (error) {
      let message = "Could not generate the policy document.";
      if (error?.response?.data instanceof Blob) {
        try {
          const data = JSON.parse(await error.response.data.text());
          message = data.detail || (data.details || []).join(" ") || message;
        } catch (_) {}
      }
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const uploadPolicyDocument = async (event) => {
    event.preventDefault();
    if (!documentForm.file) {
      toast.error("Choose a document file.");
      return;
    }
    const payload = new FormData();
    payload.append("document_type", documentForm.document_type);
    payload.append("title", documentForm.title);
    payload.append("file", documentForm.file);

    setBusy(true);
    try {
      await axiosInstance.post(
        `policies/${selected.id}/documents/`,
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success("Policy document uploaded.");
      setDocumentForm({
        document_type: "OTHER",
        title: "",
        file: null,
      });
      await open(selected.id, "documents");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Could not upload the policy document."
      );
    } finally {
      setBusy(false);
    }
  };

  const assessment = context?.status_assessment;

  return (
    <section className="maintenance-page">
      <header className="maintenance-title">
        <span>ASSURANCE OPERATIONS</span>
        <h1>Policy Maintenance</h1>
        <p>
          Operational policy actions, status evaluation and controlled
          insurance maintenance.
        </p>
      </header>

      <div className="maintenance-layout">
        <aside>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search policy or policyholder"
          />
          <div className="maintenance-list" onScroll={policyScroll}>
            {filtered.map((policy) => (
              <button
                key={policy.id}
                className={
                  selected?.id === policy.id
                    ? `active status-${policy.status.toLowerCase()}`
                    : `status-${policy.status.toLowerCase()}`
                }
                onClick={() => open(policy.id)}
              >
                <strong>{policy.code}</strong>
                <span>{policy.policyholder_name || "NO POLICYHOLDER"}</span>
                <small>{policy.plan_name} · {policy.status}</small>
                <b>{Number(policy.balance || 0).toFixed(2)}</b>
              </button>
            ))}
          </div>
        </aside>

        <main>
          {!selected ? (
            <div className="maintenance-empty">
              Select a policy to maintain.
            </div>
          ) : (
            <>
              <header className="maintenance-record">
                <div>
                  <span>POLICY MAINTENANCE RECORD</span>
                  <h2>{selected.code}</h2>
                  <p>{selected.policyholder_name}</p>
                </div>
                <div className="maintenance-actions">
                  <button
                    onClick={() =>
                      window.location.assign(
                        `/policy-payments/${selected.id}`
                      )
                    }
                  >
                    Open payment window
                  </button>
                  <button className="secondary" onClick={evaluate} disabled={busy}>
                    {busy ? "Checking..." : "Run status check"}
                  </button>
                </div>
              </header>

              <div className="maintenance-summary">
                {[
                  ["Status", selected.status],
                  ["Plan", selected.plan_name],
                  ["Policy type", selected.policy_type_name],
                  ["Monthly premium", selected.monthly_premium],
                  ["Balance", selected.balance],
                  ["Start date", selected.start_date],
                ].map(([label, value]) => (
                  <article key={label}>
                    <span>{label}</span>
                    <strong>{value ?? "—"}</strong>
                  </article>
                ))}
              </div>

              <section className="status-assessment">
                <header>
                  <div>
                    <span>POLICY STATUS ENGINE</span>
                    <h3>Current assessment</h3>
                  </div>
                  <strong
                    className={`assessment-status status-${String(
                      assessment?.recommended_status || selected.status
                    ).toLowerCase()}`}
                  >
                    {assessment?.recommended_status || selected.status}
                  </strong>
                </header>

                <div className="assessment-grid">
                  <article>
                    <span>Open bills</span>
                    <strong>{assessment?.open_bill_count ?? 0}</strong>
                  </article>
                  <article>
                    <span>Last payment</span>
                    <strong>{assessment?.last_payment_date || "—"}</strong>
                  </article>
                  <article>
                    <span>Outstanding</span>
                    <strong>{assessment?.outstanding_balance || "0.00"}</strong>
                  </article>
                  <article>
                    <span>Latest lapse</span>
                    <strong>
                      {assessment?.latest_lapse?.date_lapsed || "NONE"}
                    </strong>
                  </article>
                </div>

                <div className="assessment-reason">
                  <span>Reason</span>
                  <p>{assessment?.reason}</p>
                </div>
              </section>

              <section className="maintenance-functions">
                <h3>Maintenance functions</h3>
                <div>
                  <button className="function-button" onClick={openEditSelected}><i className="bi bi-pencil-square"/><span>Edit selected</span></button>
                  <button className="function-button" onClick={openClaim}><i className="bi bi-heart-pulse"/><span>Create claim</span></button>
                  {["INACTIVE", "LAPSED", "SUSPENDED"].includes(selected.status)&&<button className="function-button function-primary" onClick={openRejoin}><i className="bi bi-arrow-repeat"/><span>Rejoin policy</span></button>}
                  {selected.status==="ACTIVE"&&<button className="function-button function-warning" onClick={reconcileRejoinBalance} disabled={busy}><i className="bi bi-arrow-counterclockwise"/><span>Auto-clear rejoin balance</span></button>}
                  {selected.status==="ACTIVE"&&Number(selected.credit_balance||0)>0&&Number(selected.balance||0)>0&&<button className="function-button function-primary" onClick={applyAvailableCredit} disabled={busy}><i className="bi bi-cash-stack"/><span>Apply available credit</span></button>}
                  <button className="function-button function-warning" onClick={reverseLatestCreditApplication} disabled={busy}><i className="bi bi-arrow-counterclockwise"/><span>Reverse / cancel credit</span></button>
                  <button className="function-button" onClick={() => openAmendment("UPGRADE")}><i className="bi bi-arrow-up-right-circle"/><span>Upgrade policy</span></button>
                  <button className="function-button" onClick={() => openAmendment("DOWNGRADE")}><i className="bi bi-arrow-down-left-circle"/><span>Downgrade policy</span></button>
                  <button className="function-button function-warning" onClick={openMemberRemoval}><i className="bi bi-person-dash"/><span>Remove member</span></button>
                  <button className="function-button" onClick={openPolicyholderReplacement}><i className="bi bi-person-check"/><span>Replace policyholder</span></button>
                  <button className="function-button function-primary" onClick={openDocuments}><i className="bi bi-file-earmark-pdf"/><span>Policy documents</span></button>
                </div>
              </section>
            </>
          )}
        </main>
      </div>

      {rejoinBox && (
        <div className="maintenance-modal">
          <form onSubmit={submitRejoin}>
            <header>
              <div>
                <span>CONTROLLED POLICY REJOIN</span>
                <h2>Rejoin {selected?.code}</h2>
                <p>
                  Reactivate this existing policy and apply only genuine,
                  receipt-backed credit to its oldest outstanding bills.
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setRejoinBox(false)}
              >
                ×
              </button>
            </header>

            <div className="removal-form">
              <div className="removal-member-summary rejoin-summary wide">
                {[
                  ["Current status", rejoinPreview?.policy?.status],
                  ["Outstanding arrears", rejoinPreview?.credit_application?.starting_balance],
                  ["Policy credit", rejoinPreview?.credit_application?.starting_credit],
                  ["Receipt-backed credit", rejoinPreview?.credit_application?.traceable_credit],
                  ["Credit to apply", rejoinPreview?.credit_application?.applied],
                  ["Balance after rejoin", rejoinPreview?.credit_application?.remaining_balance],
                  ["Credit remaining", rejoinPreview?.credit_application?.remaining_credit],
                  ["Open bills", rejoinPreview?.credit_application?.open_bills],
                ].map(([label, value]) => (
                  <article key={label}>
                    <span>{label}</span>
                    <strong>{value ?? "—"}</strong>
                  </article>
                ))}
              </div>

              <label>
                Rejoin date
                <input
                  type="date"
                  required
                  value={rejoinForm.effective_date}
                  onChange={(event) =>
                    setRejoinForm({
                      ...rejoinForm,
                      effective_date: event.target.value,
                      confirmed: false,
                    })
                  }
                />
              </label>

              <label className="wide">
                Authorised reason
                <textarea
                  required
                  rows="3"
                  value={rejoinForm.reason}
                  onChange={(event) =>
                    setRejoinForm({
                      ...rejoinForm,
                      reason: event.target.value.toUpperCase(),
                      confirmed: false,
                    })
                  }
                  placeholder="Record why this policy is being rejoined"
                />
              </label>

              {Number(
                rejoinPreview?.credit_application?.untraceable_credit || 0
              ) > 0 && (
                <div className="removal-warning wide">
                  Untraceable credit of {rejoinPreview.credit_application.untraceable_credit}
                  {" "}is protected and will not be removed or allocated.
                </div>
              )}

              <label className="removal-confirm wide">
                <input
                  type="checkbox"
                  checked={rejoinForm.confirmed}
                  onChange={(event) =>
                    setRejoinForm({
                      ...rejoinForm,
                      confirmed: event.target.checked,
                    })
                  }
                />
                <span>
                  I confirm this is the correct existing policy. It will move
                  to ACTIVE, receipt-backed credit will clear the oldest bills,
                  and any genuine excess credit will remain on the policy.
                </span>
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setRejoinBox(false)}
              >
                Cancel
              </button>
              <button
                disabled={
                  busy || !rejoinForm.confirmed || !rejoinForm.reason.trim()
                }
              >
                {busy ? "Rejoining..." : "Confirm policy rejoin"}
              </button>
            </footer>
          </form>
        </div>
      )}




      {amendmentBox && (
        <div className="maintenance-modal">
          <form onSubmit={applyAmendment}>
            <header>
              <div>
                <span>POLICY PLAN AMENDMENT</span>
                <h2>{amendmentDirection === "UPGRADE" ? "Upgrade" : "Downgrade"} policy</h2>
                <p>
                  Every covered member will be re-graded against the selected
                  plan before any change is applied.
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setAmendmentBox(false)}
              >
                ×
              </button>
            </header>

            <div className="amendment-form">
              <label>
                Target plan
                <select
                  value={amendmentForm.plan}
                  onChange={(event) => {
                    setAmendmentForm({
                      ...amendmentForm,
                      plan: event.target.value,
                      confirmed: false,
                    });
                    setAmendmentQuote(null);
                  }}
                >
                  {(context?.available_plans || []).map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.code} · {plan.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Effective date
                <input
                  type="date"
                  required
                  value={amendmentForm.effective_date}
                  onChange={(event) => {
                    setAmendmentForm({
                      ...amendmentForm,
                      effective_date: event.target.value,
                      confirmed: false,
                    });
                    setAmendmentQuote(null);
                  }}
                />
              </label>

              <button
                type="button"
                className="secondary quote-amendment"
                onClick={quoteAmendment}
                disabled={busy}
              >
                {busy ? "Calculating..." : "Calculate and validate"}
              </button>

              {amendmentQuote && (
                <>
                  <div className="amendment-summary wide">
                    <article>
                      <span>Classification</span>
                      <strong>{amendmentQuote.direction}</strong>
                    </article>
                    <article>
                      <span>Current plan</span>
                      <strong>{amendmentQuote.current_plan.name}</strong>
                    </article>
                    <article>
                      <span>Current premium</span>
                      <strong>{amendmentQuote.current_plan.premium}</strong>
                    </article>
                    <article>
                      <span>Target plan</span>
                      <strong>{amendmentQuote.target_plan.name}</strong>
                    </article>
                    <article>
                      <span>New premium</span>
                      <strong>{amendmentQuote.target_plan.premium}</strong>
                    </article>
                    <article>
                      <span>Difference</span>
                      <strong>{amendmentQuote.premium_difference}</strong>
                    </article>
                  </div>

                  <div className="amendment-table wide">
                    <table>
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Type</th>
                          <th>Age</th>
                          <th>Current premium</th>
                          <th>New premium</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amendmentQuote.members.map((member) => (
                          <tr key={member.id}>
                            <td>{member.code} · {member.name}</td>
                            <td>{member.member_type}</td>
                            <td>{member.age}</td>
                            <td>{member.current_premium}</td>
                            <td>{member.new_premium}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="amendment-benefits wide">
                    <strong>Target-plan benefits</strong>
                    <div>
                      {amendmentQuote.benefits.length
                        ? amendmentQuote.benefits.map((benefit) => (
                            <span key={benefit.benefit_code}>
                              {benefit.benefit_code} · {benefit.description}
                            </span>
                          ))
                        : <span>No active benefits configured.</span>}
                    </div>
                  </div>
                </>
              )}

              <label className="wide">
                Authorised reason
                <textarea
                  required
                  rows="3"
                  value={amendmentForm.reason}
                  onChange={(event) =>
                    setAmendmentForm({
                      ...amendmentForm,
                      reason: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label className="removal-confirm wide">
                <input
                  type="checkbox"
                  checked={amendmentForm.confirmed}
                  onChange={(event) =>
                    setAmendmentForm({
                      ...amendmentForm,
                      confirmed: event.target.checked,
                    })
                  }
                />
                <span>
                  I confirm the target plan, every member premium and the new
                  benefit structure. Existing bills and receipts will remain
                  unchanged; the revised premium applies to future billing.
                </span>
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setAmendmentBox(false)}
              >
                Cancel
              </button>
              <button
                disabled={
                  busy ||
                  !amendmentForm.confirmed ||
                  !amendmentQuote ||
                  amendmentQuote.direction !== amendmentDirection
                }
              >
                {busy
                  ? "Applying..."
                  : `Confirm ${amendmentDirection.toLowerCase()}`}
              </button>
            </footer>
          </form>
        </div>
      )}

      {documentsBox && (
        <div className="maintenance-modal">
          <div className="policy-documents-modal">
            <header>
              <div>
                <span>POLICY DOCUMENTS</span>
                <h2>{selected?.code}</h2>
                <p>Generate official documents or manage uploaded records.</p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setDocumentsBox(false);
                  setDocumentEligibility(null);
                }}
              >
                ×
              </button>
            </header>

            <div className="documents-body">
              <section>
                <h3>Official policy documents</h3>
                <div className="generated-documents">
                  <button className="document-action" onClick={() => openGeneratedDocument("CERTIFICATE")}><i className="bi bi-award"/><span><strong>Policy certificate</strong><small>A5 official certificate</small></span></button>
                  {fixedTermCashPlan && (
                    <button
                      className={`document-action maturity-document ${maturityAvailable ? "available" : "unavailable"}`}
                      onClick={() => maturityAvailable && openGeneratedDocument("MATURITY_CERTIFICATE")}
                      disabled={!maturityAvailable || busy}
                      title={maturityReason}
                    >
                      <i className="bi bi-patch-check"/>
                      <span>
                        <strong>Maturity certificate</strong>
                        <small>{maturityReason}</small>
                      </span>
                    </button>
                  )}
                  <button className="document-action" onClick={() => openGeneratedDocument("CONFIRMATION")}><i className="bi bi-people"/><span><strong>Confirmation with dependants</strong><small>A4 acceptance and member schedule</small></span></button>
                  <button className="document-action" onClick={() => openGeneratedDocument("TERMS", "ENGLISH")}><i className="bi bi-file-text"/><span><strong>English terms and conditions</strong><small>Official one-page English policy terms</small></span></button>
                  <button className="document-action" onClick={() => openGeneratedDocument("TERMS", "NDEBELE")}><i className="bi bi-translate"/><span><strong>Ndebele terms and conditions</strong><small>Official one-page Ndebele policy terms</small></span></button>
                  <button className="document-action" onClick={() => openGeneratedDocument("ID_CARD")}><i className="bi bi-person-vcard"/><span><strong>Policyholder ID card</strong><small>Extended front card layout</small></span></button>
                </div>
              </section>

              <section>
                <h3>Uploaded documents</h3>
                <div className="uploaded-documents">
                  {(selected?.documents || []).length ? (
                    selected.documents.map((document) => (
                      <article key={document.id}>
                        <div>
                          <strong>{document.title}</strong>
                          <span>{document.document_type}</span>
                        </div>
                        <a
                          href={document.file}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      </article>
                    ))
                  ) : (
                    <p>No uploaded policy documents.</p>
                  )}
                </div>
              </section>

              <form className="document-upload" onSubmit={uploadPolicyDocument}>
                <h3>Upload supporting document</h3>
                <label>
                  Document type
                  <select
                    value={documentForm.document_type}
                    onChange={(event) =>
                      setDocumentForm({
                        ...documentForm,
                        document_type: event.target.value,
                      })
                    }
                  >
                    {[
                      "POLICY SCHEDULE",
                      "NATIONAL ID",
                      "BIRTH CERTIFICATE",
                      "MARRIAGE CERTIFICATE",
                      "BANK CONFIRMATION",
                      "CLAIM DOCUMENT",
                      "OTHER",
                    ].map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Title
                  <input
                    required
                    value={documentForm.title}
                    onChange={(event) =>
                      setDocumentForm({
                        ...documentForm,
                        title: event.target.value.toUpperCase(),
                      })
                    }
                  />
                </label>
                <label>
                  File
                  <input
                    type="file"
                    required
                    onChange={(event) =>
                      setDocumentForm({
                        ...documentForm,
                        file: event.target.files?.[0] || null,
                      })
                    }
                  />
                </label>
                <button disabled={busy}>
                  {busy ? "Uploading..." : "Upload document"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {claimBox && (
        <div className="maintenance-modal">
          <form onSubmit={submitClaim}>
            <header>
              <div>
                <span>CLAIM REGISTRATION</span>
                <h2>Create claim · {selected?.code}</h2>
                <p>
                  Claim registration snapshots the plan benefits and marks the
                  selected covered member as deceased.
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setClaimBox(false)}
              >
                ×
              </button>
            </header>

            <div className="claim-form">
              <label>
                Deceased covered member
                <select
                  required
                  value={claimForm.member_ids[0] || ""}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      member_ids: [Number(event.target.value)],
                    })
                  }
                >
                  {claimMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.code} · {member.full_name} · {member.member_type}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Date of death / incident
                <input
                  type="date"
                  required
                  value={claimForm.incident_date}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      incident_date: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Claim payment method
                <select
                  value={claimForm.payment_method}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      payment_method: event.target.value,
                    })
                  }
                >
                  <option value="">Select payment method</option>
                  {(context?.claim_payment_methods || []).map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Removal time
                <input
                  type="time"
                  value={claimForm.removal_time}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      removal_time: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Burial order number
                <input
                  value={claimForm.burial_order_number}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      burial_order_number: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label>
                Death register number
                <input
                  value={claimForm.death_register_number}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      death_register_number: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label>
                Burial place
                <input
                  value={claimForm.burial_place}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      burial_place: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label>
                Burial service by
                <input
                  value={claimForm.burial_service_by}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      burial_service_by: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label>
                Burial service date
                <input
                  type="date"
                  value={claimForm.burial_service_date}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      burial_service_date: event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Doctor / medical practitioner
                <input
                  value={claimForm.doctor_used}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      doctor_used: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label className="wide">
                Claim remarks
                <textarea
                  rows="3"
                  value={claimForm.remarks}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      remarks: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <label className="wide">
                Internal notes
                <textarea
                  rows="3"
                  value={claimForm.notes}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      notes: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>

              <div className="claim-rule-summary wide">
                <strong>Rules checked before registration</strong>
                <span>Policy must be ACTIVE.</span>
                <span>Policy balance must be fully cleared.</span>
                <span>The member must be ACTIVE and past inception.</span>
                <span>No open claim may already exist for the member.</span>
                <span>
                  The legacy three-month policy waiting period remains enforced
                  through the policy/member inception checks.
                </span>
              </div>

              <label className="removal-confirm wide">
                <input
                  type="checkbox"
                  checked={claimForm.confirmed}
                  onChange={(event) =>
                    setClaimForm({
                      ...claimForm,
                      confirmed: event.target.checked,
                    })
                  }
                />
                <span>
                  I confirm that the selected covered member is deceased and
                  that the claim information is correct. Creating the claim
                  will mark the member DECEASED and snapshot the current plan
                  and default benefits.
                </span>
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setClaimBox(false)}
              >
                Cancel
              </button>
              <button disabled={busy || !claimForm.confirmed}>
                {busy ? "Creating claim..." : "Create claim"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {replacementBox && (
        <div className="maintenance-modal">
          <form onSubmit={replacePolicyholder}>
            <header>
              <div>
                <span>CONTROLLED POLICY OPERATION</span>
                <h2>Replace policyholder</h2>
                <p>
                  Replacement is allowed only where the existing policyholder
                  is deceased.
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setReplacementBox(false)}
              >
                ×
              </button>
            </header>

            <div className="removal-form">
              <label>
                New policyholder
                <select
                  required
                  value={replacementForm.member}
                  onChange={(event) =>
                    setReplacementForm({
                      ...replacementForm,
                      member: event.target.value,
                    })
                  }
                >
                  {(context?.replacement_members || []).map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.code} · {member.full_name} · {member.member_type}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Effective date
                <input
                  type="date"
                  required
                  value={replacementForm.effective_date}
                  onChange={(event) =>
                    setReplacementForm({
                      ...replacementForm,
                      effective_date: event.target.value,
                    })
                  }
                />
              </label>

              <div className="removal-member-summary wide">
                <article>
                  <span>Selected member</span>
                  <strong>
                    {selectedReplacementMember?.full_name || "—"}
                  </strong>
                </article>
                <article>
                  <span>Current role</span>
                  <strong>
                    {selectedReplacementMember?.member_type || "—"}
                  </strong>
                </article>
                <article>
                  <span>Cellphone</span>
                  <strong>
                    {selectedReplacementMember?.cell || "MISSING"}
                  </strong>
                </article>
                <article>
                  <span>New role</span>
                  <strong>POLICY HOLDER</strong>
                </article>
              </div>

              {selectedReplacementMember?.single_plan_transition?.required && (
                <div className="replacement-note wide">
                  {selectedReplacementMember.single_plan_transition.error ? (
                    <>
                      <strong>Single Plan configuration required</strong><br />
                      {selectedReplacementMember.single_plan_transition.error}
                    </>
                  ) : (
                    <>
                      <strong>Automatic policy continuation</strong><br />
                      The policy will move to {" "}
                      <b>{selectedReplacementMember.single_plan_transition.target_plan_name}</b>
                      {" "}({selectedReplacementMember.single_plan_transition.target_plan_code})
                      {" "}and the monthly premium will change from {" "}
                      {context?.policy?.currency_code} {selectedReplacementMember.single_plan_transition.current_premium}
                      {" "}to {context?.policy?.currency_code} {selectedReplacementMember.single_plan_transition.new_premium}.
                    </>
                  )}
                </div>
              )}

              <label className="wide">
                Replacement reason
                <textarea
                  required
                  rows="4"
                  value={replacementForm.reason}
                  onChange={(event) =>
                    setReplacementForm({
                      ...replacementForm,
                      reason: event.target.value.toUpperCase(),
                    })
                  }
                  placeholder="Record the authorised reason for replacing the policyholder"
                />
              </label>

              <label className="removal-confirm wide">
                <input
                  type="checkbox"
                  checked={replacementForm.confirmed}
                  onChange={(event) =>
                    setReplacementForm({
                      ...replacementForm,
                      confirmed: event.target.checked,
                    })
                  }
                />
                <span>
                  I confirm that the former policyholder is deceased and that
                  the selected active member must become the new policyholder.
                  The policy number, password, bills, receipts, claims and
                  commission history will remain unchanged. Where only one
                  active person remains, I also approve the displayed Single
                  Plan and revised premium.
                </span>
              </label>

              <div className="replacement-note wide">
                The existing policyholder login will be reassigned to the new
                holder. The login password is preserved, but the account name,
                phone, national ID, date of birth, gender and address are
                updated from the selected member.
              </div>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setReplacementBox(false)}
              >
                Cancel
              </button>
              <button
                disabled={
                  busy ||
                  !replacementForm.confirmed ||
                  !selectedReplacementMember?.cell ||
                  Boolean(
                    selectedReplacementMember?.single_plan_transition?.error
                  )
                }
              >
                {busy
                  ? "Replacing policyholder..."
                  : "Confirm policyholder replacement"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {removeBox && (
        <div className="maintenance-modal">
          <form onSubmit={removeMember}>
            <header>
              <div>
                <span>CONTROLLED POLICY OPERATION</span>
                <h2>Remove covered member</h2>
                <p>
                  The member will remain in policy history but will no longer
                  be included in future premium billing.
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setRemoveBox(false)}
              >
                ×
              </button>
            </header>

            <div className="removal-form">
              <label>
                Active member
                <select
                  required
                  value={removeForm.member}
                  onChange={(event) =>
                    setRemoveForm({
                      ...removeForm,
                      member: event.target.value,
                    })
                  }
                >
                  {(context?.removable_members || []).map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.code} · {member.full_name} · {member.member_type}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Effective date
                <input
                  type="date"
                  required
                  value={removeForm.effective_date}
                  onChange={(event) =>
                    setRemoveForm({
                      ...removeForm,
                      effective_date: event.target.value,
                    })
                  }
                />
              </label>

              <div className="removal-member-summary wide">
                <article>
                  <span>Member</span>
                  <strong>{selectedRemovalMember?.full_name || "—"}</strong>
                </article>
                <article>
                  <span>Relationship</span>
                  <strong>
                    {selectedRemovalMember?.relationship ||
                      selectedRemovalMember?.member_type ||
                      "—"}
                  </strong>
                </article>
                <article>
                  <span>Current premium</span>
                  <strong>
                    {Number(
                      selectedRemovalMember?.monthly_premium || 0
                    ).toFixed(2)}
                  </strong>
                </article>
                <article>
                  <span>Status after action</span>
                  <strong>REMOVED</strong>
                </article>
              </div>

              <label className="wide">
                Removal reason
                <textarea
                  required
                  rows="4"
                  value={removeForm.reason}
                  onChange={(event) =>
                    setRemoveForm({
                      ...removeForm,
                      reason: event.target.value.toUpperCase(),
                    })
                  }
                  placeholder="Record the authorised reason for removing this member"
                />
              </label>

              <label className="removal-confirm wide">
                <input
                  type="checkbox"
                  checked={removeForm.confirmed}
                  onChange={(event) =>
                    setRemoveForm({
                      ...removeForm,
                      confirmed: event.target.checked,
                    })
                  }
                />
                <span>
                  I confirm that this is the correct member. Existing bills,
                  receipts, claims and history will remain unchanged. The
                  member will be excluded only from future billing.
                </span>
              </label>

              <div className="removal-warning wide">
                The policyholder cannot be removed here. Deceased members must
                be handled through the claim workflow. This action does not
                delete historical insurance records.
              </div>
            </div>

            <footer>
              <button
                type="button"
                className="secondary"
                onClick={() => setRemoveBox(false)}
              >
                Cancel
              </button>
              <button disabled={busy || !removeForm.confirmed}>
                {busy ? "Removing member..." : "Confirm member removal"}
              </button>
            </footer>
          </form>
        </div>
      )}

    </section>
  );
}

export default PolicyMaintenanceWorkspace;
